import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ShieldCheck, RefreshCw, Search, History } from "lucide-react";
import { fmtDateTime } from "@/lib/format";

const ENTITY_TYPES = [
    { value: "all", label: "All activity" },
    { value: "customer", label: "Customers" },
    { value: "invoice", label: "Invoices" },
    { value: "payment", label: "Payments" },
    { value: "route", label: "Routes" },
    { value: "rolloff_job", label: "Roll-off jobs" },
    { value: "holiday", label: "Holidays" },
    { value: "user", label: "Staff users" },
];

const ACTION_COLORS = {
    login: "bg-blue-500/10 text-blue-600 dark:text-blue-300",
    portal_login: "bg-blue-500/10 text-blue-600 dark:text-blue-300",
    portal_register: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300",
    portal_password_reset: "bg-amber-500/10 text-amber-600 dark:text-amber-300",
    apply_holiday_shift: "bg-violet-500/10 text-violet-600 dark:text-violet-300",
    payment: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300",
    refund: "bg-red-500/10 text-red-600 dark:text-red-400",
};

export default function AuditLog() {
    const [rows, setRows] = useState([]);
    const [entityType, setEntityType] = useState("all");
    const [search, setSearch] = useState("");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setBusy(true);
        try {
            const params = entityType !== "all" ? { entity_type: entityType } : {};
            const { data } = await api.get("/audit-log", { params });
            setRows(data || []);
        } finally { setBusy(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [entityType]);

    const filtered = rows.filter((r) => {
        if (!search) return true;
        const blob = `${r.actor_email || ""} ${r.action || ""} ${r.entity_type || ""} ${r.entity_id || ""}`.toLowerCase();
        return blob.includes(search.toLowerCase());
    });

    return (
        <div data-testid="audit-log-page">
            <PageHeader
                title="Audit Log"
                subtitle="Every meaningful change in the platform, recorded automatically."
                actions={
                    <Button variant="outline" size="sm" onClick={load} disabled={busy} data-testid="audit-refresh">
                        <RefreshCw className={`w-4 h-4 mr-1 ${busy ? "animate-spin" : ""}`} />Refresh
                    </Button>
                }
            />
            <div className="px-8 py-6 space-y-4">
                <Card>
                    <CardContent className="p-4 flex flex-col md:flex-row gap-3 md:items-center">
                        <div className="flex items-center gap-2 flex-1">
                            <Search className="w-4 h-4 text-muted-foreground" />
                            <Input value={search} onChange={(e) => setSearch(e.target.value)}
                                   placeholder="Search by actor, action, or entity id…"
                                   data-testid="audit-search" />
                        </div>
                        <Select value={entityType} onValueChange={setEntityType}>
                            <SelectTrigger className="md:w-48" data-testid="audit-entity-filter">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {ENTITY_TYPES.map((t) => (
                                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-0">
                        <div className="p-5 border-b border-border flex items-center justify-between">
                            <div>
                                <div className="overline mb-1">Stream</div>
                                <h3 className="font-heading text-lg font-semibold">Recent activity</h3>
                            </div>
                            <div className="text-xs text-muted-foreground font-mono">{filtered.length} events</div>
                        </div>
                        <div className="divide-y divide-border" data-testid="audit-list">
                            {filtered.length === 0 && (
                                <div className="p-10 text-center text-sm text-muted-foreground">
                                    <History className="w-8 h-8 mx-auto mb-3 opacity-40" />
                                    No activity matches your filters.
                                </div>
                            )}
                            {filtered.map((r) => {
                                const color = ACTION_COLORS[r.action] || "bg-secondary text-foreground";
                                return (
                                    <div key={r.id} className="p-4 flex items-start gap-4 hover:bg-secondary/40 transition-colors"
                                         data-testid={`audit-row-${r.id}`}>
                                        <div className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${color}`}>
                                            {r.action}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm">
                                                <span className="font-medium">{r.actor_email || "system"}</span>
                                                <span className="text-muted-foreground"> · {r.entity_type}</span>
                                                <span className="text-muted-foreground font-mono"> · {(r.entity_id || "").slice(0, 8)}</span>
                                            </div>
                                            {r.metadata && Object.keys(r.metadata).length > 0 && (
                                                <pre className="text-[11px] text-muted-foreground mt-1 font-mono whitespace-pre-wrap break-words">
                                                    {JSON.stringify(r.metadata, null, 0)}
                                                </pre>
                                            )}
                                        </div>
                                        <div className="text-[11px] text-muted-foreground shrink-0">
                                            {fmtDateTime(r.created_at)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
                <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" />Audit logs are immutable — created automatically when staff or customers perform meaningful actions.
                </div>
            </div>
        </div>
    );
}
